import React, { useState, useRef, useEffect, memo, Suspense } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import Control from '../store/control';
import Param from '../store/param';
import { ToCoord3D } from '../shape/Intersect';
import { GetMaterial } from '../fileIO/fileLoader';
import { SystemUpdate, ThreeDRotation } from '@material-ui/icons';
import { drawIntersectionPoints, getContours} from './utils';

// fileExt: 0:PLY 1:STL
const MSocketLoad = memo(({ 
  meshSocketLoad, 
  clickable, 
  showWire, 
  showTransparent,
  fileExt, 
  opacityLeg=1.0, 
  color
}) => {

  const ref        = useRef();
  const activeStep    = Param.Get('activeStep');
  const align         = Param.Get('align');

  const xAngle        = Control.Get("xAngle");
  const yAngle        = Control.Get("yAngle");
  const zAngle        = Control.Get("zAngle");
  const redrawAngle   = Control.Get('redrawAngle');
  const setControl    = Control.Get('setControl');
  const toCenter      = Control.Get('toCenter');
  const toModifyCenter  = Control.Get('toModifyCenter');
  const toModifyYheight = Control.Get('toModifyYheight');
  const origin        = Control.Get('origin');


  const isCenterX   = (activeStep == 1) && (align.step == 2);

  
  const [objects, setObjects] = useState([]);
  const [cAngle, setCAngle]   = useState([xAngle, yAngle, zAngle]);
  const [didModifyCenter, setDidModifyCenter] = useState(0); // TODO: make it much smarter
  const { camera, mouse } = useThree();


  function setOriginToCenter(mesh) {
    var center = getCenterPoint(mesh);
    console.log("bounding box center = (" + center.x + ", " + center.y + ", " + center.z + ")");
    mesh.geometry.center();

    setControl(s => {s.meshSocketLoad = mesh});
  }

  function setModifyCenter(mesh) {
    if (!didModifyCenter) {
      mesh.geometry.attributes.position.needsUpdate = true;
      for (var i = 0; i < mesh.geometry.attributes.position.array.length; i++) {
        if (i%3 == 0) {
          mesh.geometry.attributes.position.array[i] -= origin.x;
        } else if (i%3 == 1) {
          mesh.geometry.attributes.position.array[i] -= origin.z;
        } else if (i%3 == 2) {
          mesh.geometry.attributes.position.array[i] -= origin.y;
        }
      }
      setDidModifyCenter(true); // TODO: it is effective only at once, so if user move back to the previous step, not effective.
    }
    setControl(s => {s.meshSocketLoad = mesh});
  }

  function setModifyYheight(mesh) {
    mesh.geometry.attributes.position.needsUpdate = true;
    for (var i = 0; i < mesh.geometry.attributes.position.array.length; i++) {
      if (i%3 == 2) {
        mesh.geometry.attributes.position.array[i] -= origin.y;
      }
    }

    setControl(s => {s.meshSocketLoad = mesh});
  }


  function getCenterPoint(mesh) {
    var geometry = mesh.geometry;
    geometry.computeBoundingBox();

    var center = new THREE.Vector3();
    geometry.boundingBox.getCenter(center);

    mesh.localToWorld( center );

    return center;
  }

  const onClick = () => {
    var pt = ToCoord3D(meshSocketLoad, camera, mouse);
    if(pt){
      console.log('Leg click (' + mouse.x.toFixed(2) + ", " + mouse.y.toFixed(2) + ") --> ("+ pt.x.toFixed(2) + ", " + pt.y.toFixed(2) + ", " + pt.z.toFixed(2) + ")");
      setControl(s => {s.click = pt});
    }
  };

  // set leg mesh
  useEffect(() => {
    console.log('clickable = ' + clickable);

    meshSocketLoad.rotation.x = -Math.PI / 2; // opengl is different coordinates, so rotate 90 degree
    //meshSocketLoad.material = new THREE.MeshBasicMaterial( { color: color, side: THREE.DoubleSide, transparent: true, opacity: 0.5 } )
    meshSocketLoad.material = new THREE.MeshMatcapMaterial( { color: color, side: THREE.DoubleSide, transparent: true, opacity: 0.6 } )
    if ( meshSocketLoad != null ) {
      if (objects.length >= 1) {
        setObjects(objects.pop()); // remove current object
      }

      if (clickable) {
        setObjects([<primitive key='leg' ref={ref} object={meshSocketLoad} onClick={onClick} />])
      } else{ 
        setObjects([<primitive key='leg' ref={ref} object={meshSocketLoad} />]);
      }
      console.log("Leg verices = " + meshSocketLoad.geometry.attributes.position.count);
      
    }
  }, [meshSocketLoad, clickable])


  useEffect(() => {
    var refMesh = ref.current;
    if (refMesh) {
      refMesh.geometry.rotateX(Math.PI / 180 * (xAngle - cAngle[0]));
      refMesh.geometry.rotateY(Math.PI / 180 * (yAngle - cAngle[1]));
      refMesh.geometry.rotateZ(Math.PI / 180 * (zAngle - cAngle[2]));
      setControl(s => {s.redrawBox += 1, s.redrawAngle = false});
      setCAngle([xAngle, yAngle, zAngle]);
    }
  }, [redrawAngle])
  

  useEffect(() => {
    var refMesh = ref.current;
    if (refMesh) {
      /*
      if (showWire) {
        refMesh.material = GetMaterial('WIREFRAME');
      } else if (showTransparent) {
        //refMesh.material = GetMaterial('TRANSPARENT');
        refMesh.material.transparent = true;
        refMesh.material.opacity = 0.1;
      } else {
        if(fileExt == 0){ // PLY
          refMesh.material = GetMaterial('VERTEX');
        }else{            // STL
          refMesh.material = GetMaterial('NORMAL');
        }
        refMesh.material.transparent = true;
        refMesh.material.opacity = opacityLeg;
      }
      */
    }
  }, [showWire, showTransparent])


  useEffect(() => {
    var refMesh = ref.current;  
    if ( refMesh && toCenter ) {
      setOriginToCenter(refMesh);
      setControl(s => {s.toCenter = false});
    }
  }, [toCenter])

  useEffect(() => {
    var refMesh = ref.current;
    if ( refMesh && toModifyCenter ) {
      setModifyCenter(refMesh);
      setControl(s => {s.toModifyCenter = false});
    }
  }, [toModifyCenter])

  useEffect(() => {
    var refMesh = ref.current;
    if ( refMesh && toModifyYheight ) {
      setModifyYheight(refMesh);
      setControl(s => {s.toModifyYheight = false});
    }
  }, [toModifyYheight])


  // Get contour for stump length
  useEffect(() => {
    var refMesh = ref.current;

    if (refMesh && isCenterX) {
      var plane = new THREE.Plane(new THREE.Vector3(1,0,0), -origin.x);
      var vertices = drawIntersectionPoints(refMesh, plane, origin.x);
      
      try {
        var contours = getContours(vertices, [], true);
        setControl(s => {s.projectPoints = contours[0]});
      } catch (error) {
        console.log(error);
      }
    }
  }, [origin]);

  
  const isObject = (objects.length != 0); 

  return (
    <>
      { isObject ? objects : null }
    </>
  )
});

export default MSocketLoad
